import {getLocaleNumberSymbol, NumberSymbol} from '@angular/common';
import {Directive, ElementRef, forwardRef, HostListener, Input, Renderer2} from '@angular/core';
import {DefaultValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {TranslateService} from '@ngx-translate/core';
import {NumberTypeEnum} from './number-type.enum';

@UntilDestroy()
@Directive({
    selector: 'input[mydocma-number-field-input]',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            // When Angular is setting up dependency injection it sometimes needs
            // to refer to classes or tokens that aren't yet fully defined
            // forwardRef gives chance to angualr it will be defined later
            useExisting: forwardRef(() =>  NumberFieldInputDirective),
            multi: true
        }
    ]
})
export class NumberFieldInputDirective extends DefaultValueAccessor {

    @Input()
    numberField: any;
    private localDecimal = '.';
    private replaceDecimal = this.localDecimal === '.' ? ',' : '.'

    constructor(renderer: Renderer2, private readonly elementRef: ElementRef<any>,
                private translateService: TranslateService) {
        super(renderer, elementRef, false)
        this.initDecimalSettings();
        this.translateService.onLangChange
            .pipe(untilDestroyed(this))
            .subscribe(l => {
                this.initDecimalSettings();
                this.writeValue(this.getControlValue());
            });
    }

    @HostListener('input', ['$event.currentTarget.value'])
    onInput = (event: any) => {
    };

    @HostListener('paste', ['$event'])
    onPaste($event: ClipboardEvent): void {

        const clipboardValue = $event.clipboardData.getData('text/plain');
        if (!clipboardValue) {
            return
        }
        const clipboardNumber = this.parseNumber(clipboardValue);
        if (isNaN(clipboardNumber)) {
            $event.preventDefault();
            return
        }

        const selectionText = this.getSelectionText();
        const selectionStart = ($event.target as any).selectionStart || 0;
        const controlValue = this.getControlValue();
        const controlNumber = this.parseNumber(controlValue)


        if (this.numberField.fieldWrapper.config.numberType === NumberTypeEnum.integer && !Number.isInteger(clipboardNumber)) {
            $event.preventDefault();
            return;
        }

        if (selectionText?.length === controlValue?.length) {
            // using base functionality
            this.onInput(clipboardValue);
            $event.preventDefault();
            return;
        }

        if (!Number.isInteger(controlNumber) && !Number.isInteger(clipboardNumber)) {
            const convertedNum = (clipboardNumber * Math.pow(10, this.decimalCount(clipboardNumber))).toString();
            const conText = controlValue.substring(0, selectionStart) + convertedNum + controlValue.substring(selectionStart);
            this.onInput(conText);
            $event.preventDefault()
            return;

        }
    }

    writeValue(value: any): void {
        let formatedValue = this.parseValue(value?.toString());
        if (formatedValue) {
            if (this.numberField.fieldWrapper.config?.numberType === NumberTypeEnum.currency) {
                formatedValue = this.truncateDecimal(formatedValue);
            }
            const controlValue: string = this.getControlValue();
            if (controlValue) {
                const lastChar = controlValue.substring(controlValue.length - 1);
                if (lastChar === this.localDecimal || lastChar === this.replaceDecimal) {
                    if (controlValue.substring(0, controlValue.length - 1) === formatedValue) {
                        // control value not changed
                        return;
                    }
                }
            }
        }
        super.writeValue(formatedValue)
    }


    registerOnChange(fn: any): void {
        this.onInput = (value) => {

            let parsedValue = this.parseValue(value);
            if (this.numberField.fieldWrapper.config?.numberType === NumberTypeEnum.currency) {
                parsedValue = this.truncateDecimal(parsedValue);
            }

            if (value && value !== parsedValue) {
                super.setProperty('value', parsedValue);
            }
            if (parsedValue === '-') {
                return
            }

            const numberValue = this.parseNumber(parsedValue);
            fn(numberValue);
        };
    }

    private truncateDecimal(value: string): string {
        if (!value) {
            return;
        }
        const decimalPointIndex = value.lastIndexOf(this.localDecimal);
        if (decimalPointIndex < 0) {
            return value;
        }
        let fractionPart = value.substring(decimalPointIndex + 1);
        if (fractionPart.length <= 2) {
            return value;
        }
        fractionPart = fractionPart.substring(0, 2);
        const truncatedValue = value.substring(0, decimalPointIndex + 1) + fractionPart;
        return truncatedValue;
    }



    private initDecimalSettings() {
        this.localDecimal = getLocaleNumberSymbol(this.translateService.currentLang, NumberSymbol.Decimal);
        this.replaceDecimal = this.localDecimal === '.' ? ',' : '.'
    }

    private getSelectionText(): string {
        const controlValue = this.getControlValue()
        if (controlValue) {
            return controlValue.substring(
                this.elementRef.nativeElement.selectionStart,
                this.elementRef.nativeElement.selectionEnd);
        }
        return null;
    }

    private decimalCount(num: number): number {
        const numStr = String(num);
        if (numStr.includes(this.localDecimal)) {
            return numStr.split(this.localDecimal)[1].length;
        }
        return 0;
    }

    private getControlValue(): string {
        return this.elementRef.nativeElement.value;
    }

    private parseValue(valueToParse: string): string {
        if (!valueToParse) {
            return null;
        }


        let parsedValue = valueToParse.replace(this.replaceDecimal, this.localDecimal)
            .replace(`${this.localDecimal}${this.replaceDecimal}`, this.localDecimal)
            .replace(`${this.replaceDecimal}${this.localDecimal}`, this.localDecimal);
        if (!parsedValue) return null;

        if (parsedValue.split(this.localDecimal).length > 2) {
            parsedValue = parsedValue.split(this.localDecimal).slice(0, 2).join(this.localDecimal);
        }
        return parsedValue;
    }

    private parseNumber(valueToParse: string): number {
        return !valueToParse ? null : parseFloat(valueToParse.replace(this.localDecimal, '.'));
    }


}
